import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    ViewChild
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef
} from '@angular/material/dialog';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import * as L from 'leaflet';

export type MapResult = {
    lat: number;
    lng: number;
    displayName: string | null;
};

type SearchResult = {
    lat: string;
    lon: string;
    display_name: string;
};

@Component({
    selector: 'app-map-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        TranslatePipe
    ],
    templateUrl: './map-dialog.component.html',
    styleUrls: ['./map-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapDialogComponent
    implements AfterViewInit, OnDestroy {

    @ViewChild('mapContainer', { static: true })
    mapContainer!: ElementRef<HTMLDivElement>;

    map!: L.Map;
    marker?: L.Marker;

    static readonly DENMARK_CENTER = {
        lat: 56.26392,
        lng: 9.501785,
        zoom: 6
    };

    initial?: {
        lat: number;
        lng: number;
        displayName?: string;
    };

    searchQuery = '';
    searchResults: SearchResult[] = [];
    searchError = '';
    searching = false;

    manualLat: number | null = null;
    manualLng: number | null = null;

    selectedDisplayName: string | null = null;

    private lastSearchAt = 0;
    private readonly europeCountryCodes = [
        'al', 'ad', 'at', 'by', 'be', 'ba', 'bg',
        'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr',
        'de', 'gr', 'hu', 'is', 'ie', 'it', 'xk',
        'lv', 'li', 'lt', 'lu', 'mt', 'md', 'mc',
        'me', 'nl', 'mk', 'no', 'pl', 'pt', 'ro',
        'ru', 'sm', 'rs', 'sk', 'si', 'es', 'se',
        'ch', 'tr', 'ua', 'gb', 'va'
    ].join(',');

    private readonly markerIcon = L.icon({
        iconUrl:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(`
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="36"
                    height="48"
                    viewBox="0 0 36 48">

                    <path
                        d="
                            M18 0
                            C8.06 0 0 8.06 0 18
                            C0 31.5 18 48 18 48
                            C18 48 36 31.5 36 18
                            C36 8.06 27.94 0 18 0
                            Z
                        "
                        fill="#0F79F3"
                    />

                    <circle
                        cx="18"
                        cy="18"
                        r="7"
                        fill="white"
                    />
                </svg>
            `),

        iconSize: [36, 48],
        iconAnchor: [18, 48],
        popupAnchor: [0, -44]
    });

    constructor(
        private translate: TranslateService,
        private dialogRef: MatDialogRef<
            MapDialogComponent,
            MapResult
        >,

        @Inject(MAT_DIALOG_DATA)
        public data: any
    ) {
        this.initial = data?.initial;
        this.searchQuery = this.initial?.displayName ?? '';
    }

    ngAfterViewInit(): void {
        const center = this.initial
            ? {
                lat: this.initial.lat,
                lng: this.initial.lng,
                zoom: 14
            }
            : MapDialogComponent.DENMARK_CENTER;

        this.map = L.map(
            this.mapContainer.nativeElement,
            {
                center: [
                    center.lat,
                    center.lng
                ],
                zoom: center.zoom,
                zoomControl: true
            }
        );

        L.tileLayer(
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            {
                maxZoom: 19,
                attribution:
                    '&copy; OpenStreetMap contributors',
                crossOrigin: 'anonymous'
            }
        ).addTo(this.map);

        if (this.initial) {
            this.manualLat = this.initial.lat;
            this.manualLng = this.initial.lng;

            this.placeOrMoveMarker(
                this.initial.lat,
                this.initial.lng,
                this.initial.displayName ?? null
            );
        }

        this.map.on(
            'click',
            (event: L.LeafletMouseEvent) => {

                const {
                    lat,
                    lng
                } = event.latlng;

                this.manualLat = lat;
                this.manualLng = lng;

                this.searchQuery = '';
                this.searchResults = [];

                this.placeOrMoveMarker(
                    lat,
                    lng,
                    null
                );
            }
        );

        // Important for Angular Material dialog animation.
        // Prevents partially grey / incorrectly sized map.
        setTimeout(() => {
            this.map.invalidateSize(true);
        }, 250);
    }

    private placeOrMoveMarker(
        lat: number,
        lng: number,
        displayName: string | null = null
    ): void {

        this.selectedDisplayName = displayName;

        if (!this.marker) {
            this.marker = L.marker(
                [lat, lng],
                {
                    draggable: true,
                    icon: this.markerIcon
                }
            ).addTo(this.map);

            this.marker.on(
                'dragend',
                () => this.onMarkerMoved()
            );
        } else {
            this.marker.setLatLng([
                lat,
                lng
            ]);
        }

        const popupText =
            displayName ||
            this.formatLatLngPopup(
                lat,
                lng
            );

        this.marker
            .bindPopup(popupText)
            .openPopup();
    }

    private onMarkerMoved(): void {
        if (!this.marker) {
            return;
        }

        const position =
            this.marker.getLatLng();

        this.manualLat = position.lat;
        this.manualLng = position.lng;

        // Marker was manually moved,
        // so the previous address may no longer be valid.
        this.searchQuery = '';
        this.selectedDisplayName = null;

        this.marker
            .bindPopup(
                this.formatLatLngPopup(
                    position.lat,
                    position.lng
                )
            )
            .openPopup();
    }

    formatLatLngPopup(
        lat: number,
        lng: number
    ): string {
        const latitudeLabel = this.translate.instant('Latitude');
        const longitudeLabel = this.translate.instant('Longitude');

        return `
            ${latitudeLabel}: ${lat.toFixed(6)},
            ${longitudeLabel}: ${lng.toFixed(6)}
        `;
    }

    // ---------------------------
    // SEARCH
    // ---------------------------

    async searchPlace(): Promise<void> {
        const query =
            this.searchQuery.trim();

        if (!query || this.searching) {
            return;
        }

        this.searchError = '';
        this.searchResults = [];
        this.searching = true;

        try {
            /**
             * Respect the public Nominatim
             * 1 request/sec limit.
             */
            const elapsed =
                Date.now() - this.lastSearchAt;

            if (elapsed < 1000) {
                await new Promise(resolve =>
                    setTimeout(
                        resolve,
                        1000 - elapsed
                    )
                );
            }

            this.lastSearchAt =
                Date.now();

            const params = new URLSearchParams({
                format: 'jsonv2',
                q: query,
                limit: '5',
                countrycodes: this.europeCountryCodes,
                addressdetails: '1',
                'accept-language': this.getSearchLanguage()
            });
            const response =
                await fetch(
                    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
                    {
                        headers: {
                            Accept: 'application/json'
                        }
                    }
                );

            if (!response.ok) {
                throw new Error(
                    `Search failed: ${response.status}`
                );
            }

            const result =
                await response.json();

            this.searchResults =
                Array.isArray(result)
                    ? result
                    : [];

            if (!this.searchResults.length) {
                this.searchError =
                    this.translate.instant('No_locations_found');
            }
        } catch (error) {
            console.error(
                '[MapDialog] location search failed:',
                error
            );

            this.searchError =
                this.translate.instant('Location_search_failed');
        } finally {
            this.searching = false;
        }
    }

    selectSearchResult(
        result: SearchResult
    ): void {

        const lat =
            Number(result.lat);

        const lng =
            Number(result.lon);

        if (
            !Number.isFinite(lat) ||
            !Number.isFinite(lng)
        ) {
            return;
        }

        this.searchQuery =
            result.display_name;

        this.searchResults = [];
        this.searchError = '';

        this.manualLat = lat;
        this.manualLng = lng;

        this.placeOrMoveMarker(
            lat,
            lng,
            result.display_name
        );

        this.map.setView(
            [lat, lng],
            16
        );
    }

    // ---------------------------
    // MANUAL LAT / LNG
    // ---------------------------

    goToCoordinates(): void {
        const lat =
            Number(this.manualLat);

        const lng =
            Number(this.manualLng);

        if (
            !Number.isFinite(lat) ||
            lat < -90 ||
            lat > 90
        ) {
            this.searchError =
                this.translate.instant('Invalid_latitude');

            return;
        }

        if (
            !Number.isFinite(lng) ||
            lng < -180 ||
            lng > 180
        ) {
            this.searchError =
                this.translate.instant('Invalid_longitude');

            return;
        }

        this.searchError = '';
        this.searchQuery = '';
        this.searchResults = [];

        this.placeOrMoveMarker(
            lat,
            lng,
            null
        );

        this.map.setView(
            [lat, lng],
            16
        );
    }

    // ---------------------------
    // CURRENT LOCATION
    // ---------------------------

    locateMe(): void {
        if (!navigator.geolocation) {
            this.searchError =
                this.translate.instant('Geolocation_not_supported');

            return;
        }

        this.searchError = '';
        this.searchQuery = '';
        this.searchResults = [];

        this.map.locate({
            setView: true,
            maxZoom: 16,
            enableHighAccuracy: true
        });

        this.map.once(
            'locationfound',
            (event: L.LocationEvent) => {

                const {
                    lat,
                    lng
                } = event.latlng;

                this.manualLat = lat;
                this.manualLng = lng;

                this.placeOrMoveMarker(
                    lat,
                    lng,
                    null
                );
            }
        );

        this.map.once(
            'locationerror',
            () => {
                this.searchError =
                    `${this.translate.instant('Geolocation_failed')}. ${this.translate.instant('Location_permission_hint')}`;
            }
        );
    }

    useCurrent(): void {
        if (!this.marker) {
            return;
        }

        const position =
            this.marker.getLatLng();

        this.dialogRef.close({
            lat: position.lat,
            lng: position.lng,
            displayName:
                this.selectedDisplayName
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private getSearchLanguage(): string {
        const lang = (
            this.translate.getCurrentLang() ||
            this.translate.getFallbackLang() ||
            'en'
        ).toLowerCase();

        return lang.startsWith('da') ? 'da' : 'en';
    }

    ngOnDestroy(): void {
        if (!this.map) {
            return;
        }

        this.map.off();
        this.map.remove();
    }
}